import { getResource } from "../../../language/resources/getResource";
import { formatDate, formatTimeOnly } from "../../../language/time";
import { LiftAccessRuleSet } from "./models";

/**
 * Display strings for the bulk-edit review. The label helpers take one rule (the
 * resulting values, off the change set); the `*Lines` helpers take a whole rule set
 * (the current values).
 */

type Resources = Record<string, any>;

type Dateish = Date | string | null | undefined;

// Structural, so the label helpers take either a persisted rule (current values)
// or an editable one off the change set (resulting values).
type BlackoutRangeLike = {
  blackoutDateStart?: Dateish;
  blackoutDateEnd?: Dateish;
  blackoutTimeSlotStart?: Dateish;
  blackoutTimeSlotEnd?: Dateish;
};

type DayOfWeekLike = {
  dayOfWeekId: number | null;
  blackoutTimeSlotStart?: Dateish;
  blackoutTimeSlotEnd?: Dateish;
};

export const EM_DASH = "–";
export const MISSING = "—";
export const SHARED_MARK = "*";

const SHORT_WEEKDAY_KEYS: Record<number, string> = {
  0: "SHORT_DAY_OF_WEEK_SUN",
  1: "SHORT_DAY_OF_WEEK_MON",
  2: "SHORT_DAY_OF_WEEK_TUE",
  3: "SHORT_DAY_OF_WEEK_WED",
  4: "SHORT_DAY_OF_WEEK_THU",
  5: "SHORT_DAY_OF_WEEK_FRI",
  6: "SHORT_DAY_OF_WEEK_SAT",
};

export const cleanLines = (values: (string | undefined | null)[]): string[] =>
  values.filter((v): v is string => !!v);

export const shortDay = (
  dayOfWeekId: number | null | undefined,
  resources: Resources,
): string => {
  const key = dayOfWeekId != null ? SHORT_WEEKDAY_KEYS[dayOfWeekId] : undefined;
  return key ? getResource(resources, key) : MISSING;
};

/**
 * Formatting dominates the review scan: ~300ms with this cache over 5,000 rule sets,
 * ~1,250ms without. The win is repetition *within* a rule set (its dates format
 * several times per scan), so it holds even when every set's dates are unique.
 */
const FORMAT_CACHE_LIMIT = 20000;
const formatCache = new Map<string, string | undefined>();

// Kind-prefixed: a Date's epoch and a numeric-looking string would otherwise collide.
const cacheKey = (value: Date | string, format: string): string =>
  value instanceof Date
    ? `D${value.getTime()}|${format}`
    : `S${value}|${format}`;

const formatCached = (
  key: string,
  compute: () => string | undefined,
): string | undefined => {
  // has(), not get(): undefined is a real answer worth caching.
  if (formatCache.has(key)) return formatCache.get(key);

  const formatted = compute();
  // Stop filling at the bound rather than clearing — clearing on every miss would be
  // slower than no cache once a selection exceeds it.
  if (formatCache.size < FORMAT_CACHE_LIMIT) formatCache.set(key, formatted);
  return formatted;
};

/** Prefer these over the raw formatters anywhere per-rule-set. */
export const formatDateValue = (
  value: Date | string,
  format: string,
): string | undefined =>
  formatCached(cacheKey(value, `d:${format}`), () => formatDate(value, format));

export const formatTimeValue = (
  value: Date | string,
  format: string,
): string | undefined =>
  formatCached(cacheKey(value, `t:${format}`), () =>
    formatTimeOnly(value, format, true),
  );

export const fmtDate = (d: Dateish, dateFormat: string): string =>
  (d && formatDateValue(d, dateFormat)) || MISSING;

export const fmtTime = (d: Dateish, timeFormat: string): string =>
  (d && formatTimeValue(d, timeFormat)) || MISSING;

export const dateRangeText = (
  start: Dateish,
  end: Dateish,
  dateFormat: string,
): string =>
  `${fmtDate(start, dateFormat)} ${EM_DASH} ${fmtDate(end, dateFormat)}`;

/** Time range like "5 AM – 9 PM"; undefined when either bound is missing. */
export const timeRangeText = (
  start: Dateish,
  end: Dateish,
  timeFormat: string,
): string | undefined => {
  if (!start || !end) return undefined;
  const s = formatTimeValue(start, timeFormat);
  const e = formatTimeValue(end, timeFormat);
  return s && e ? `${s} ${EM_DASH} ${e}` : undefined;
};

/** Max-days value with a trailing shared-usage marker. */
export const maxDaysLabel = (
  numberOfDays: number | null | undefined,
  sharedUsage: boolean | undefined,
): string | undefined =>
  numberOfDays == null
    ? undefined
    : `${numberOfDays}${sharedUsage ? SHARED_MARK : ""}`;

/** A blackout date range as "start – end (time)", matching the Access Overview tooltip. */
export const blackoutRangeLabel = (
  r: BlackoutRangeLike,
  dateFormat: string,
  timeFormat: string,
): string | undefined => {
  if (!r.blackoutDateStart || !r.blackoutDateEnd) return undefined;
  const dates = dateRangeText(
    r.blackoutDateStart,
    r.blackoutDateEnd,
    dateFormat,
  );
  const time = timeRangeText(
    r.blackoutTimeSlotStart,
    r.blackoutTimeSlotEnd,
    timeFormat,
  );
  return time ? `${dates} (${time})` : dates;
};

/** Blackout days-of-week grouped by shared time slot: "Mo, Tu, We (12 AM – 11:59 PM)". */
export const blackoutDowGroupedLabels = (
  rules: DayOfWeekLike[],
  resources: Resources,
  timeFormat: string,
): string[] => {
  const groups = new Map<string, { time: string; dayIds: number[] }>();
  for (const r of rules) {
    if (r.dayOfWeekId == null) continue;
    const time =
      timeRangeText(r.blackoutTimeSlotStart, r.blackoutTimeSlotEnd, timeFormat) ??
      "";
    const group = groups.get(time) ?? { time, dayIds: [] };
    if (!group.dayIds.includes(r.dayOfWeekId)) group.dayIds.push(r.dayOfWeekId);
    groups.set(time, group);
  }
  return [...groups.values()].map((g) => {
    const days = g.dayIds
      .slice()
      .sort((a, b) => a - b)
      .map((id) => shortDay(id, resources))
      .join(", ");
    return g.time ? `${days} (${g.time})` : days;
  });
};

// --- Whole-rule-set convenience (current values) --------------------------

export const effectiveStartLines = (
  rs: LiftAccessRuleSet,
  dateFormat: string,
): string[] =>
  cleanLines(
    (rs.effectiveDateRangeRules ?? []).map((r) =>
      formatDateValue(r.effectiveDateStart, dateFormat),
    ),
  );

export const effectiveEndLines = (
  rs: LiftAccessRuleSet,
  dateFormat: string,
): string[] =>
  cleanLines(
    (rs.effectiveDateRangeRules ?? []).map((r) =>
      formatDateValue(r.effectiveDateEnd, dateFormat),
    ),
  );

export const effectiveTimeLines = (
  rs: LiftAccessRuleSet,
  timeFormat: string,
): string[] =>
  cleanLines(
    (rs.effectiveDateRangeRules ?? []).map((r) =>
      timeRangeText(r.effectiveTimeSlotStart, r.effectiveTimeSlotEnd, timeFormat),
    ),
  );

export const maxDaysLines = (rs: LiftAccessRuleSet): string[] =>
  cleanLines(
    (rs.maxDaysRules ?? []).map((r) =>
      maxDaysLabel(r.numberOfDays, r.sharedUsage),
    ),
  );

export const blackoutRangeLines = (
  rs: LiftAccessRuleSet,
  dateFormat: string,
  timeFormat: string,
): string[] =>
  cleanLines(
    (rs.blackoutDateRangeRules ?? [])
      .slice()
      .sort(
        (a, b) =>
          new Date(a.blackoutDateStart).getTime() -
          new Date(b.blackoutDateStart).getTime(),
      )
      .map((r) => blackoutRangeLabel(r, dateFormat, timeFormat)),
  );

export const blackoutDowLines = (
  rs: LiftAccessRuleSet,
  resources: Resources,
  timeFormat: string,
): string[] =>
  blackoutDowGroupedLabels(
    rs.blackoutDayOfWeekRules ?? [],
    resources,
    timeFormat,
  );
